import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createDuel } from '../../api';
import HrnbForm from '../controls/HrnbForm';
import HrnbInput from '../controls/HrnbInput';
import HrnbTextArea from '../controls/HrnbTextArea';
import HrnbSelect from '../controls/HrnbSelect';
import HrnbButton from '../controls/HrnbButton';
import HrnbAlert from '../controls/HrnbAlert';
import { PlusIcon, BackIcon } from '../../icons';
import '../../css/duels/DuelForm.css';

/**
 * Форма создания дуэли
 */
function DuelForm() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [format, setFormat] = useState('SOLO');
    const [roundType, setRoundType] = useState('SINGLE_ROUND');
    const [votingSystem, setVotingSystem] = useState('');
    const [criteriaPreset, setCriteriaPreset] = useState('');

    // Опции форматов (актуальные значения enum DuelFormat)
    const formatOptions = [
        { value: 'SOLO', label: '1 на 1 (SOLO)' },
        { value: 'TEAM_2V2', label: '2 на 2 (TEAM_2V2)' },
        { value: 'TEAM_3V3', label: '3 на 3 (TEAM_3V3)' }
    ];

    // Опции типов раундов (актуальные значения enum DuelRoundType)
    const roundTypeOptions = [
        { value: 'SINGLE_ROUND', label: '1 раунд (SINGLE_ROUND)' },
        { value: 'BEST_OF_THREE', label: 'До 2 побед (BEST_OF_THREE)' }
    ];

    // Опции систем голосования
    const votingSystemOptions = [
        { value: '', label: 'Не указано (по умолчанию)' },
        { value: 'PLUS_MINUS_ZERO', label: '+/0/- (Плюс / Ноль / Минус)' },
        { value: 'NUMERIC_10', label: '0-10 (Числовая шкала)' },
        { value: 'CRITERIA_BASED', label: 'Критериальная система' }
    ];

    // Опции пресетов критериев
    const criteriaPresetOptions = [
        { value: '', label: 'Выберите пресет' },
        { value: 'SCORE_5_5_2', label: '5/5/2 (Техника 5 + Текст 5 + Впечатление 2, макс. 12)' },
        { value: 'SCORE_6_5_3', label: '6/5/3 (Техника 6 + Текст 5 + Впечатление 3, макс. 14)' },
        { value: 'SCORE_5_3_1', label: '5/3/1 (Техника 5 + Текст 3 + Впечатление 1, макс. 9)' },
        { value: 'SCORE_4_3_2', label: '4/3/2 (Техника 4 + Текст 3 + Впечатление 2, макс. 9)' },
        { value: 'SCORE_3_2_1', label: '3/2/1 (Техника 3 + Текст 2 + Впечатление 1, макс. 6)' }
    ];

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const formData = new FormData(e.target);
            // Минимальные данные для создания дуэли
            const duelData = {
                title: formData.get('title'),
                description: formData.get('description'),
                format: formData.get('format'),
                roundType: formData.get('roundType')
            };

            // Добавляем систему голосования если указана
            if (votingSystem) {
                duelData.votingSystem = votingSystem;
            }
            // Добавляем пресет критериев если указан и система критериальная
            if (votingSystem === 'CRITERIA_BASED' && criteriaPreset) {
                duelData.criteriaPreset = criteriaPreset;
            }

            console.log('[DuelForm] Отправка данных:', duelData);
            const createdDuel = await createDuel(duelData);
            console.log('[DuelForm] Дуэль создана:', createdDuel);
            navigate(`/duels/${createdDuel.id}`);
        } catch (err) {
            console.error('Ошибка при создании дуэли:', err);
            console.error('Ошибка details:', err.response?.data);
            console.error('Статус:', err.response?.status);
            console.error('Заголовки:', err.response?.headers);
            setError(
                err.response?.data?.message || 
                'Не удалось создать дуэль. Попробуйте позже.'
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="duel-form">
            <h2 className="duel-form__title">Создать дуэль</h2>
            
            {error && (
                <HrnbAlert 
                    variant="danger" 
                    onClose={() => setError(null)}
                >
                    {error}
                </HrnbAlert>
            )}

            <HrnbForm onSubmit={handleSubmit} className="duel-form__form">
                <HrnbInput
                    name="title"
                    label="Название дуэли"
                    placeholder="Например: Битва за район"
                    required
                    maxLength={100}
                />

                <HrnbTextArea
                    name="description"
                    label="Описание"
                    placeholder="Опишите условия дуэли..."
                    rows={4}
                    maxLength={500}
                />

                <HrnbSelect
                    label="Формат"
                    options={formatOptions}
                    value={format}
                    onChange={setFormat}
                    required
                />
                <input type="hidden" name="format" value={format} />

                <HrnbSelect
                    label="Тип раундов"
                    options={roundTypeOptions}
                    value={roundType}
                    onChange={setRoundType}
                    required
                />
                <input type="hidden" name="roundType" value={roundType} />

                <HrnbSelect
                    label="Система голосования"
                    options={votingSystemOptions}
                    value={votingSystem}
                    onChange={setVotingSystem}
                />

                {votingSystem === 'CRITERIA_BASED' && (
                    <HrnbSelect
                        label="Пресет критериев *"
                        options={criteriaPresetOptions}
                        value={criteriaPreset}
                        onChange={setCriteriaPreset}
                        required
                    />
                )}

                <div className="duel-form__actions">
                    <HrnbButton 
                        type="submit" 
                        variant="primary"
                        disabled={loading}
                    >
                        <PlusIcon style={{ width: 16, height: 16, marginRight: 8 }} />
                        {loading ? 'Создание...' : 'Создать дуэль'}
                    </HrnbButton>
                    <HrnbButton 
                        type="button" 
                        variant="secondary"
                        onClick={() => navigate('/duels')}
                    >
                        <BackIcon style={{ width: 16, height: 16, marginRight: 8 }} />
                        Отмена
                    </HrnbButton>
                </div>
            </HrnbForm>
        </div>
    );
}

export default DuelForm;
